import { Button, Cell, Grid, HFlow, InfoLabel, Tooltip, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import {
  CheckboxField,
  createDecorator,
  Form,
  FormRenderProps,
  NumberField,
  PecFormApi,
  resetAllFieldStates,
  SelectField,
  SubmitButton,
} from 'components/form'
import { resolveValue } from 'components/form/core/hooks/useField'
import { CidSelectField } from 'components/form/field/select/CidSelectField/CidSelectField'
import { css } from 'emotion'
import { TamanhoFraldaGeriatricaEnum, TipoAtestadoEnum } from 'graphql/types.generated'
import { Fragment, useMemo, useState } from 'react'
import { metaPath } from 'util/metaPath'

import { AtestadoAssinaturaDigitalView } from '../assinatura-digital/AtestadoAssinaturaDigitalView'
import { AtestadoConfirmarAssinaturaDigitalModal } from '../assinatura-digital/AtestadoConfirmarAssinaturaDigitalModal'
import { MESSAGE_TOOLTIP_SUBMIT_ATESTADO_DISABLED } from '../assinatura-digital/model-atestadoAssinaturaDigital'
import { useAtestadoAssinaturaDigitalForm } from '../assinatura-digital/useAtestadoAssinaturaDigitalForm'
import { AtestadoContentBox } from '../components/AtestadoContentBox'
import AtestadoModel, { CidadaoAtestado } from '../components/model-atestado'
import { useAtestadoCancelarCadastro } from '../hooks/useAtestadoCancelarCadastro'
import { tituloAtestado } from '../utils-atestado'
import { createSolicitacaoFraldasGeriatricasCalculations } from './calculator-solicitacaoFraldasGeriatricas'
import { tamanhosFralda } from './model-solicitacaoFraldasGeriatricas'
import { getSolicitacaoFraldasGeriatricasText, parseUnidadesPorDia } from './utils-solicitacaoFraldasGeriatricas'
import { solicitacaoFraldasGeriatricasFormValidator } from './validator-solicitacaoFraldasGeriatricas'

const path = metaPath<AtestadoModel>()

export interface SolicitacaoFraldasGeriatricasFormProps {
  atendimentoId: ID
  prontuarioId: ID
  cidadao: CidadaoAtestado
  dataAtendimento: Instant
  onSubmit(values: AtestadoModel): void
}

export function SolicitacaoFraldasGeriatricasForm(props: Readonly<SolicitacaoFraldasGeriatricasFormProps>) {
  const { atendimentoId, prontuarioId, cidadao, dataAtendimento, onSubmit } = props

  const alert = useAlert()
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [isEmailCidadaoDirty, setIsEmailCidadaoDirty] = useState(false)
  const [isEmailCidadaoCanceled, setIsEmailCidadaoCanceled] = useState(false)

  const tipoAtestado = TipoAtestadoEnum.SOLICITACAO_FRALDAS_GERIATRICAS
  const possuiCpfCns = !!(cidadao.cpf || cidadao.cns)

  const decorators = useMemo(
    () => [createDecorator(...createSolicitacaoFraldasGeriatricasCalculations(path, cidadao))],
    [cidadao]
  )

  const handleSubmit = (values: AtestadoModel, formApi: PecFormApi) => {
    onSubmit(values)
    setTimeout(formApi.reset)
    resetAllFieldStates(formApi)
    alert('success', 'Atestado salvo com sucesso.')
  }

  const {
    emailCidadao,
    handleSubmitWrapper,
    isAssinando,
    isModalOpen,
    onConfirm,
    onCancel,
    errorMessage,
    onAlertClose,
  } = useAtestadoAssinaturaDigitalForm({
    cidadaoId: cidadao.id,
    atendimentoId,
    prontuarioId,
    tipoAtestado,
    dataAtendimento,
    onSubmit: handleSubmit,
  })

  const handleCancel = useAtestadoCancelarCadastro({
    isEmailCidadaoDirty,
    setIsEmailCidadaoCanceled,
    successMessage: 'Cadastro de solicitação de fraldas geriátricas cancelado com sucesso.',
  })

  const renderForm = (formProps: FormRenderProps<AtestadoModel>) => {
    const values = resolveValue(formProps.values, path) || {}
    const totalUnidadesValue = values.unidadesPorDia ? values.unidadesPorDia * 30 : '-'

    return (
      <Fragment>
        <Cell size={8}>
          {!possuiCpfCns && (
            <Grid gap={1}>
              <Cell size={4}>
                <NumberField
                  name={path.cpfCns}
                  label='CPF/CNS'
                  disabled={values.naoPossui}
                  required={!values.naoPossui}
                  maxLength={15}
                />
              </Cell>
              <Cell>
                <HFlow style={styles.naoPossuiContainer}>
                  <CheckboxField name={path.naoPossui} label='Não possui' />
                </HFlow>
              </Cell>
            </Grid>
          )}
        </Cell>

        <Cell size={3}>
          <SelectField<TamanhoFraldaGeriatricaEnum>
            name={path.tamanhoFralda}
            label='Tamanho da fralda'
            items={tamanhosFralda}
            itemToString={(item) => item}
            required
          />
        </Cell>

        <Cell size={3}>
          <NumberField
            name={path.unidadesPorDia}
            label='Recomendação diária'
            maxLength={3}
            required
            parse={parseUnidadesPorDia}
          />
        </Cell>

        <Cell size={2}>
          <Tooltip text='Recomendação diária informada multiplicada por 30 dias ao mês'>
            <span className={styles.tooltipSpan}>
              <InfoLabel title='Total de unidades' childStyles={styles.totalUnidadesInfoLabelChild}>
                {totalUnidadesValue}
              </InfoLabel>
            </span>
          </Tooltip>
        </Cell>

        <Cell size={4}>
          <CidSelectField name={path.cid10} label='CID10' renderGrupoCiapRelacionada={false} />
        </Cell>

        <AtestadoAssinaturaDigitalView
          name={path.assinadoDigitalmente}
          cidadaoId={cidadao.id}
          cidadaoEmail={emailCidadao}
          onEmailCidadaoChange={setIsSubmitDisabled}
          onDirtyChange={setIsEmailCidadaoDirty}
          hasCid10={!!formProps.values.cid10}
          style={styles.assinaturaDigital}
          emailCanceled={isEmailCidadaoCanceled}
          onEmailCancel={setIsEmailCidadaoCanceled}
        />

        <Cell size={12}>
          <VFlow>
            <AtestadoContentBox title={tituloAtestado(tipoAtestado)} content={values.descricao} />
            <HFlow justifyContent='flex-end'>
              <Button size='small' onClick={() => handleCancel(formProps.form)}>
                Cancelar
              </Button>

              <Tooltip text={isSubmitDisabled ? MESSAGE_TOOLTIP_SUBMIT_ATESTADO_DISABLED : undefined}>
                <SubmitButton
                  throttleSubmit
                  type='button'
                  size='small'
                  kind='primary'
                  handleSubmit={formProps.handleSubmit}
                  disabled={isSubmitDisabled || isAssinando}
                  loading={isAssinando}
                >
                  Salvar atestado
                </SubmitButton>
              </Tooltip>
            </HFlow>
          </VFlow>
        </Cell>

        {isModalOpen && (
          <AtestadoConfirmarAssinaturaDigitalModal
            emailCidadao={emailCidadao}
            onConfirm={onConfirm}
            onCancel={onCancel}
            isAssinando={isAssinando}
            errorMessage={errorMessage}
            onAlertClose={onAlertClose}
            tipoAtestado={tipoAtestado}
            descricao={formProps.values.descricao}
          />
        )}
      </Fragment>
    )
  }

  return (
    <Form<AtestadoModel>
      initialValues={{ descricao: getSolicitacaoFraldasGeriatricasText({ cidadao }), tipo: tipoAtestado }}
      render={renderForm}
      onSubmit={handleSubmitWrapper}
      validate={solicitacaoFraldasGeriatricasFormValidator(possuiCpfCns)}
      decorators={decorators}
    />
  )
}

const styles = {
  naoPossuiContainer: css`
    margin-top: 1.75rem;
  `,
  assinaturaDigital: css`
    padding-left: 1rem;
  `,
  totalUnidadesInfoLabelChild: css`
    font-size: 1rem;
    padding-top: 0.5rem;
  `,
  tooltipSpan: css`
    display: inline-block;
  `,
}
